import { useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Clock } from "lucide-react";

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", type: "general" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(lang === "fr" ? "Message envoyé · Nous revenons vers vous sous 24h" : "Message sent · We'll be in touch within 24h");
      setForm({ name: "", email: "", subject: "", message: "", type: "general" });
    } catch {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20" data-testid="contact-page">
        <Reveal>
          <div className="max-w-3xl">
            <div className="eyebrow text-[var(--dsf-orange)] mb-3">{t("nav.contact")}</div>
            <h1 className="font-serif text-5xl md:text-7xl text-[var(--dd-green)] leading-[1.02] mb-6">{t("contact.title")}</h1>
            <p className="text-[var(--dsf-text-secondary)] text-lg leading-relaxed">{t("contact.subtitle")}</p>
          </div>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Contact info */}
          <Reveal className="lg:col-span-4">
            <div className="dd-bg-bone text-[var(--dd-green)] p-8 lg:p-10 h-full">
              <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3">House</div>
              <h2 className="font-serif text-3xl mb-8">Dakar Street Food</h2>

              <div className="space-y-6 text-sm">
                <div className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 text-[var(--dsf-orange)] shrink-0" /><div>423 Lewis Avenue<br />Brooklyn, NY 11233<br />United States</div></div>
                <div className="flex items-start gap-3"><Phone size={16} className="mt-0.5 text-[var(--dsf-orange)] shrink-0" /><div>+1 (718) 555-0142<br />+1 (917) 555-0143 (WhatsApp)</div></div>
                <div className="flex items-start gap-3"><Mail size={16} className="mt-0.5 text-[var(--dsf-orange)] shrink-0" /><div>hello@dakarstreetfood.com<br />press@dakarstreetfood.com</div></div>
                <div className="flex items-start gap-3"><Clock size={16} className="mt-0.5 text-[var(--dsf-orange)] shrink-0" /><div>{lang === "fr" ? "Tous les jours" : "Open daily"}<br />11:00 AM - 11:00 PM</div></div>
              </div>

              <div className="border-t border-black/8 mt-10 pt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] mb-2">{lang === "fr" ? "Demandes spéciales" : "Special requests"}</div>
                <p className="text-sm text-[var(--dsf-text)]">{lang === "fr" ? "Pour les services traiteur, demandes presse ou partenariats, utilisez le formulaire en sélectionnant la catégorie correspondante." : "For catering, press requests or partnerships, use the form with the right category."}</p>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-8 card-light p-8 lg:p-12" data-testid="contact-form">
            <Reveal>
              <h3 className="font-serif text-2xl text-[var(--dd-green)] mb-8">{lang === "fr" ? "Écrivez-nous" : "Write to us"}</h3>
            </Reveal>

            <div className="mb-6">
              <Label>{lang === "fr" ? "Type de demande" : "Request type"}</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {[
                  { v: "general", l: lang === "fr" ? "Général" : "General" },
                  { v: "catering", l: lang === "fr" ? "Traiteur" : "Catering" },
                  { v: "press", l: "Presse" },
                  { v: "partnership", l: lang === "fr" ? "Partenariat" : "Partnership" },
                ].map((o) => (
                  <button key={o.v} type="button" onClick={() => setForm({ ...form, type: o.v })} className={`px-4 py-2.5 text-xs uppercase tracking-wider transition-all ${form.type === o.v ? "dd-bg-bone text-[var(--dd-green)]" : "border border-black/5 hover:border-[var(--dsf-green)]"}`} data-testid={`contact-type-${o.v}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <Label>{t("common.name")}</Label>
                <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
              </div>
              <div>
                <Label>{t("common.email")}</Label>
                <Input type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
              </div>
            </div>

            <div className="mb-5">
              <Label>{t("common.subject")}</Label>
              <Input value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} required testid="contact-subject" />
            </div>

            <div className="mb-8">
              <Label>{t("common.message")}</Label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={6} className="w-full border border-black/5 px-4 py-3 mt-2 text-sm focus:border-[var(--dsf-green)] bg-white" data-testid="contact-message" />
            </div>

            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="contact-submit">
              <Send size={14} /> {loading ? t("common.loading") : t("common.send")}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Label = ({ children }) => <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] block">{children}</label>;
const Input = ({ value, onChange, type = "text", required, testid }) => (
  <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-black/5 px-4 py-3 mt-2 text-sm focus:border-[var(--dsf-green)] bg-white" data-testid={testid} />
);
